import { CreditCardIcon, DownloadIcon, ListUnorderedIcon, SyncIcon } from "@primer/octicons-react";
import { Box, IconButton, Link } from "@primer/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hocSinhApi } from "../../../api/hocSinhApi";
import DataGrid from "../../../components/data-grid";
import { IDataGridColumn } from "../../../components/data-grid/type/IDataGridProps";
import HocSinhShortInfoNew from "../../../components/hoc-sinh/HocSinhShortInfoNew";
import SelectBoxTrangThaiDangHoc, { StudentFilterType } from "../../../components/selectbox-trangthai-danghoc";
import SelectBoxTrangThaiHocSinhMultiple from "../../../components/selectbox-trangthai-hocsinh-multiple";
import Button from "../../../components/ui/button";
import Text from "../../../components/ui/text";
import TextWithEllipse from "../../../components/ui/text-with-ellipse";
import { useCommonContext } from "../../../contexts/common";
import { thuPhiTabAction } from "../../../state/actions/thu-phi-tab/thuPhiTabAction";
import { RootState } from "../../../state/reducers";
import CongNoModal from "./CongNoModal";

interface DanhSachHocSinhPageProps {
  dataSourceFilter: any[];
  isLoading: boolean;
  onReload: () => void;
  truongFilter: number;
  khoiFilter: number;
  heFilter: number;
  onFilterChange: {
    setTruongFilter: (value: number) => void;
    setKhoiFilter: (value: number) => void;
    setHeFilter: (value: number) => void;
  };
}

const DanhSachHocSinhPage = ({
  dataSourceFilter,
  isLoading,
  onReload,
  truongFilter,
  khoiFilter,
  heFilter,
  onFilterChange
}: DanhSachHocSinhPageProps) => {
  const { translate } = useCommonContext();
  const dispatch = useDispatch();
  const { nam_hoc, dm_coso_id } = useSelector((x: RootState) => x.common);
  const { tabs } = useSelector((x: RootState) => x.thuPhiTab);
  const { user_info } = useSelector((x: RootState) => x.auth);
  const allowedTruongIds = useMemo(() => {
    return user_info?.campus?.map((c) => c.id) ?? [];
  }, [user_info]);
  const pendingHocSinhDataRef = useRef<{ data: any; timestamp: number } | null>(null);
  const [congNoModalOpen, setCongNoModalOpen] = useState(false);
  const [selectedHocSinh, setSelectedHocSinh] = useState<{ id: number; ho_ten?: string } | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedStatusIds, setSelectedStatusIds] = useState<number[]>([]);
  const [statusList, setStatusList] = useState<any[]>([]);
  const [filterStatusType, setFilterStatusType] = useState<StudentFilterType>("all");

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await hocSinhApi.selectTrangThai();
        if (res.is_success && res.data) {
          setStatusList(res.data);
        }
      } catch (err) {
        console.error("Error fetching student status:", err);
      }
    };
    fetchStatus();
  }, []);

  const isDuTuyenStatus = (s: any) => {
    return (
      s.is_dutuyen === 1 ||
      s.is_dutuyen === true ||
      s.is_dutuyen === "1" ||
      s.isDuTuyen === 1 ||
      s.isDuTuyen === true ||
      s.isDuTuyen === "1"
    );
  };

  const duTuyenStatusIds = useMemo(() => {
    return statusList.filter(isDuTuyenStatus).map((s: any) => s.id);
  }, [statusList]);

  const duTuyenStatusNames = useMemo(() => {
    return statusList
      .filter(isDuTuyenStatus)
      .map((s: any) => s.trang_thai?.trim().toLowerCase());
  }, [statusList]);

  const filteredDataSource = useMemo(() => {
    if (!dataSourceFilter || !Array.isArray(dataSourceFilter)) return [];
    let result = dataSourceFilter;

    // Lọc theo combo "Hiển thị học sinh đang học" vs "Hiển thị toàn bộ học sinh"
    if (filterStatusType === "dang_hoc" && statusList.length > 0) {
      result = result.filter((item: any) => {
        if (item.dm_trangthaihocsinh_id && duTuyenStatusIds.includes(item.dm_trangthaihocsinh_id)) {
          return true;
        }
        if (item.trang_thai && duTuyenStatusNames.includes(item.trang_thai.trim().toLowerCase())) {
          return true;
        }
        if (item.isDuTuyen === true || item.is_dutuyen === true || item.isDuTuyen === 1 || item.is_dutuyen === 1) {
          return true;
        }
        return false;
      });
    }

    if (selectedStatusIds.length > 0) {
      const selectedStatusNames = statusList
        .filter((s) => selectedStatusIds.includes(s.id))
        .map((s) => s.trang_thai?.trim().toLowerCase());

      result = result.filter((item: any) => {
        if (item.dm_trangthaihocsinh_id && selectedStatusIds.includes(item.dm_trangthaihocsinh_id)) {
          return true;
        }
        if (item.trang_thai && selectedStatusNames.includes(item.trang_thai.trim().toLowerCase())) {
          return true;
        }
        return false;
      });
    }

    return result;
  }, [dataSourceFilter, filterStatusType, statusList, duTuyenStatusIds, duTuyenStatusNames, selectedStatusIds]);

  useEffect(() => {
    if (pendingHocSinhDataRef.current && tabs.length > 0) {
      const newTab = tabs.find((t) => t.title === "Untitled" && !t.hocSinh);

      if (newTab) {
        dispatch(thuPhiTabAction.CHANGEG_SELECTED_TAB(newTab.id));
      }
    }
  }, [tabs, dispatch]);

  useEffect(() => {
    if (pendingHocSinhDataRef.current && tabs.length > 0) {
      const newTab = tabs.find((t) => t.title === "Untitled" && !t.hocSinh);

      if (newTab && pendingHocSinhDataRef.current.data) {
        const { data } = pendingHocSinhDataRef.current;

        const hocSinhData = {
          id: data.ts_hocsinh_id || data.id || 0,
          ma_hs: data.ma_hs || "",
          ho_ten: data.ho_ten || "",
          dm_gioitinh_id: data.dm_gioitinh_id || 0,
          ngay_sinh: data.ngay_sinh || null,
          nam_hoc: data.nam_hoc || nam_hoc,
          ten_truong: data.ten_truong || "",
          ten_truong_en: data.ten_truong_en || "",
          ten_khoi: data.ten_khoi || "",
          ten_khoi_en: data.ten_khoi_en || "",
          ten_he: data.ten_he || "",
          ten_he_en: data.ten_he_en || "",
          ten_lop: data.ten_lop || "",
          ...data
        };

        dispatch(thuPhiTabAction.UPDATE_TAB_DATA({
          ...newTab,
          title: data.ho_ten || "Untitled",
          hocSinh: hocSinhData,
          request: {
            nam_hoc: nam_hoc,
            ts_hocsinh_id: hocSinhData.id
          }
        }));

        pendingHocSinhDataRef.current = null;
      }
    }
  }, [tabs, dispatch, nam_hoc]);

  const handleNopPhi = useCallback((data: any) => {
    const hocSinhId = data.ts_hocsinh_id || data.id || 0;
    const existingTab = tabs.find((tab) =>
      tab.hocSinh && (tab.hocSinh.id === hocSinhId || tab.hocSinh.ts_hocsinh_id === hocSinhId)
    );
    if (existingTab) {
      dispatch(thuPhiTabAction.CHANGEG_SELECTED_TAB(existingTab.id));
      return;
    }
    pendingHocSinhDataRef.current = {
      data: data,
      timestamp: Date.now()
    };
    dispatch(thuPhiTabAction.ADD_NEW_TAB(undefined));
  }, [dispatch, tabs]);

  const handleEditClick = useCallback((data: any) => { }, []);

  const handleExport = useCallback(async () => {
    try {
      setIsExporting(true);
      const res = await hocSinhApi.exportStudentInformation({
        nam_hoc: nam_hoc,
        dm_coso_id: dm_coso_id,
        dm_truong_id: truongFilter,
        dm_he_id: heFilter,
        dm_khoi_id: khoiFilter,
        student_filter_type: filterStatusType,
        dm_trangthaihocsinh_ids: selectedStatusIds.length > 0 ? selectedStatusIds : undefined,
        dm_truong_ids: truongFilter === 0 && allowedTruongIds.length > 0 ? allowedTruongIds : undefined,
      });
      if (res && res.data) {
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `DanhSachHocSinh_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setIsExporting(false);
    }
  }, [nam_hoc, dm_coso_id, truongFilter, heFilter, khoiFilter, filterStatusType, selectedStatusIds, allowedTruongIds]);

  const columns = useMemo<IDataGridColumn[]>(() => {
    const quanHes = [
      {
        id: 1,
        text: "Mẹ",
      },
      {
        id: 2,
        text: "Bố",
      },
      {
        id: 3,
        text: "Giám hộ",
      },
    ];
    return [
      {
        id: "cmd",
        width: 90,
        caption: translate("Thao tác"),
        align: "center",
        fixed: "left",
        cellRender: (data: any) => {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
              <IconButton
                icon={CreditCardIcon}
                variant="invisible"
                size="small"
                aria-label={translate("Nộp phí")}
                onClick={() => handleNopPhi(data)}
              />
              <IconButton
                icon={ListUnorderedIcon}
                variant="invisible"
                size="small"
                aria-label={translate("Công nợ")}
                onClick={() => {
                  setSelectedHocSinh({ id: data.ts_hocsinh_id, ho_ten: data.ho_ten });
                  setCongNoModalOpen(true);
                }}
              />
            </Box>
          );
        },
      },
      {
        id: "trang_thai",
        dataField: "trang_thai",
        width: 150,
        caption: translate("Trạng thái"),
        isMainColumn: true,
        cellRender: (data: any) => {
          let color: any = "fg.default";
          switch (data.trang_thai) {
            case "New Enrollment":
              color = "accent.emphasis";
              break;
            case "Thôi học":
              color = "danger.emphasis";
              break;
            case "Học sinh lên lớp chưa đóng phí":
              color = "attention.emphasis";
              break;
            default:
              break;
          }
          return (
            <Box
              sx={{
                color: color,
              }}
            >
              <TextWithEllipse text={data.trang_thai || ""} lineNumber={1} />
            </Box>
          );
        },
      },
      {
        id: "ma_hs",
        dataField: "ma_hs",
        width: 100,
        caption: translate("Mã HS"),
        fixed: "left",
        isMainColumn: true,
        allowCopy: true,
        cellRender: (data: any) => {
          return (
            <Box
              sx={{ color: "accent.fg", fontWeight: 600, cursor: "pointer" }}
              onClick={() => handleNopPhi(data)}
            >
              {data.ma_hs}
            </Box>
          );
        },
      },
      {
        id: "ma_tn",
        dataField: "ma_tn",
        width: 100,
        caption: translate("Mã TN"),
        fixed: "left",
        isMainColumn: true,
        allowCopy: true,
        cellRender: (data: any) => {
          return (
            <Box
              sx={{ color: "accent.fg", fontWeight: 600, cursor: "pointer" }}
              onClick={() => handleNopPhi(data)}
            >
              {data.ma_tn}
            </Box>
          );
        },
      },
      {
        id: "ho_ten",
        dataField: "ho_ten",
        width: 200,
        caption: translate("Học sinh"),
        fixed: "left",
        calculateCellValue: (data: any) => {
          return [
            data.ho_ten,
            data.ngay_sinh,
          ].filter(Boolean).join(" ");
        },
        allowCopy: true,
        cellRender: (data: any) => {
          return <HocSinhShortInfoNew data={data} onClick={handleEditClick} />;
        },
      },
      {
        id: "ten_lop",
        dataField: "ten_lop",
        caption: translate("Trường lớp"),
        width: 150,
        calculateCellValue: (data: any) => {
          return [
            data.ten_lop,
            data.ten_khoi,
            data.ten_he ? `Hệ ${data.ten_he}` : "",
          ].filter(Boolean).join(", ");
        },
        cellRender: (data: any) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "baseline",
              }}
            >
              <Box sx={{ display: "flex" }}>
                <Box sx={{ fontWeight: 600 }}>{data.ten_lop || ""}</Box>
              </Box>
              <Box sx={{ color: "fg.muted" }}>
                {data.ten_khoi || ""}{data.ten_khoi || data.ten_he ? ", " : ""}{data.ten_he ? `Hệ ${data.ten_he}` : ""}
              </Box>
            </Box>
          );
        },
      },

      ...quanHes.map((qh: any) => ({
        id: `Group_${qh.id}`,
        caption: translate(`Thông tin ${qh.text}`),
        columns: [
          {
            id: `HT_${qh.id}`,
            dataField: `HT_${qh.id}`,
            width: 150,
            caption: translate("Họ tên"),
            allowCopy: true,
            cellRender: (data: any) => {
              return (
                <Link href="#" sx={{ textDecoration: "none", fontWeight: 600 }}>
                  {data[`HT_${qh.id}`] || ""}
                </Link>
              );
            },
          },
          {
            id: `DT_${qh.id}`,
            dataField: `DT_${qh.id}`,
            width: 110,
            caption: translate("Điện thoại"),
            allowCopy: true,
          },
          {
            id: `EM_${qh.id}`,
            dataField: `EM_${qh.id}`,
            width: 180,
            caption: translate("Email"),
            allowCopy: true,
            cellRender: (data: any) => {
              return (
                <TextWithEllipse
                  text={data[`EM_${qh.id}`] || ""}
                  lineNumber={1}
                />
              );
            },
          },
        ],
      })),
      {
        id: "Group_ThongTinHocSinh",
        caption: translate("Thông tin học sinh"),
        columns: [
          {
            id: "dia_chi_tt",
            dataField: "dia_chi_tt",
            width: 200,
            caption: translate("Địa chỉ thường trú"),
            allowCopy: true,
            cellRender: (data: any) => {
              return (
                <TextWithEllipse
                  text={data.dia_chi_tt || ""}
                  lineNumber={1}
                />
              );
            },
          },
          {
            id: "dia_chi_hientai",
            dataField: "dia_chi_hientai",
            width: 200,
            caption: translate("Địa chỉ hiện tại"),
            allowCopy: true,
            cellRender: (data: any) => {
              return (
                <TextWithEllipse
                  text={data.dia_chi_hientai || ""}
                  lineNumber={1}
                />
              );
            },
          },
          {
            id: "ma_dinh_danh",
            dataField: "ma_dinh_danh",
            width: 140,
            caption: translate("Mã định danh"),
            allowCopy: true,
          },
        ],
      },
    ];
  }, [translate, handleNopPhi, handleEditClick]);
  return (
    <React.Fragment>
      <Box sx={{ p: 1 }}>
        <Box className="col-md-12" sx={{ mt: 1 }}>
          <DataGrid
            title={<Text text="Danh sách học sinh" sx={{ fontSize: 16, fontWeight: 600 }} />}
            height={window.innerHeight - 200}
            dataSource={filteredDataSource}
            isLoading={isLoading}
            filterRow={{
              enable: true
            }}
            searchEnable={true}
            actionComponent={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <SelectBoxTrangThaiDangHoc
                  value={filterStatusType}
                  defaultValue="all"
                  onValueChanged={(val) => setFilterStatusType(val)}
                  size="small"
                />
                <SelectBoxTrangThaiHocSinhMultiple
                  selectedValue={selectedStatusIds}
                  onSelectionChanged={(ids) => setSelectedStatusIds(ids)}
                  hocSinhs={dataSourceFilter}
                  size="small"
                  maxWidth={250}
                  isShowClearBtn={true}
                />
                <Button
                  text={translate("Xuất Excel")}
                  leadingVisual={DownloadIcon}
                  variant="default"
                  size="small"
                  isLoading={isExporting}
                  onClick={handleExport}
                />
                <Button
                  text={translate("Làm mới")}
                  leadingVisual={SyncIcon}
                  variant="default"
                  size="small"
                  isLoading={isLoading}
                  onClick={onReload}
                />
              </Box>
            }
            columns={columns}
            paging={{
              enable: true,
              pageSize: 50,
              pageSizeItems: [50, 100, 200, 500, 1000]
            }}
          />
        </Box>
      </Box>
      {selectedHocSinh && (
        <CongNoModal
          isOpen={congNoModalOpen}
          onClose={() => {
            setCongNoModalOpen(false);
            setSelectedHocSinh(null);
          }}
          ts_hocsinh_id={selectedHocSinh.id}
          nam_hoc={nam_hoc}
          ho_ten={selectedHocSinh.ho_ten}
        />
      )}
    </React.Fragment>
  );
};
export default DanhSachHocSinhPage;


